import { Database, DollarSign, FileText } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../components/ui/card";
import { OperationConfirmDialog } from "../../../../../components/ui/operation-confirm-dialog";
import { formatDateBR } from "../../../../../utils/format-utils";
import { EntregoBaixaItem } from "../../fund-baixa-resolution/types";
import { BaixaStatCard } from "../../fund-baixa-resolution/components/baixa-stat-card";
import { RealizarBaixaDialog } from "./realizar-baixa-dialog";

interface ImileBaixaItemCardProps {
  baixaItem: EntregoBaixaItem;
  selectedOps: string[];
  onStartDoc: (filterDate: string, listOps: string[]) => void;
  onSubmitDb: (filterDate: string, listOps: string[]) => void;
  isStartingDoc: boolean;
  isSubmittingDb: boolean;
}

export function ImileBaixaItemCard({
  baixaItem,
  selectedOps,
  onStartDoc,
  onSubmitDb,
  isStartingDoc,
  isSubmittingDb,
}: ImileBaixaItemCardProps) {
  const [openStartDoc, setOpenStartDoc] = useState(false);
  const [openSubmitDb, setOpenSubmitDb] = useState(false);

  // O card é chaveado por data_romaneio, que é o filterDate mandado ao backend
  // (a baixa da iMile, como a do entrego, é por data de romaneio + lista de ops).
  const filterDate = baixaItem.data_romaneio.slice(0, 10);

  // Os DOIS botões ficam sempre habilitados. No entrego o "Realizar Baixa" tem
  // disabled={!isDatePassed}, o que travava o caso legítimo de baixa antecipada;
  // aqui a antecipação é decisão de mesa, e o aviso no popup é a barreira.
  // Comparação em meia-noite local dos dois lados, para o horário não fazer o
  // próprio dia do vencimento contar como antecipado.
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const vencimento = new Date(
    `${baixaItem.data_vencimento.slice(0, 10)}T00:00:00`
  );
  const isAntecipada = hoje < vencimento;
  const diasAteVencimento = Math.max(
    0,
    Math.round((vencimento.getTime() - hoje.getTime()) / 86400000)
  );

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value / 100);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-lg">
              Data Romaneio: {filterDate}
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              Vencimento: {formatDateBR(baixaItem.data_vencimento.slice(0, 10))}
              {isAntecipada && (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  baixa antecipada
                </span>
              )}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpenStartDoc(true)}
              disabled={isStartingDoc}
            >
              <FileText className="mr-2 h-4 w-4" />
              Criar CNAB
            </Button>
            <Button
              size="sm"
              onClick={() => setOpenSubmitDb(true)}
              disabled={isSubmittingDb}
            >
              <Database className="mr-2 h-4 w-4" />
              Realizar Baixa
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <BaixaStatCard
            title="Valor de Face"
            value={formatCurrency(baixaItem.valor_face)}
            icon={<DollarSign className="h-6 w-6" />}
          />
          <BaixaStatCard
            title="Valor de Compra"
            value={formatCurrency(baixaItem.valor_compra)}
            icon={<DollarSign className="h-6 w-6" />}
          />
          <BaixaStatCard
            title="Deságio"
            value={formatCurrency(baixaItem.desagio)}
            icon={<DollarSign className="h-6 w-6" />}
          />
        </div>
      </CardContent>

      {/* Criar CNAB não move dinheiro, então segue a confirmação padrão. */}
      <OperationConfirmDialog
        open={openStartDoc}
        onOpenChange={setOpenStartDoc}
        onConfirm={() => onStartDoc(filterDate, selectedOps)}
        action="baixa"
        ops={selectedOps.join(", ")}
        date={filterDate}
        details={[{ label: "Etapa", value: "Criar CNAB de baixa" }]}
        loading={isStartingDoc}
      />

      <RealizarBaixaDialog
        open={openSubmitDb}
        onOpenChange={setOpenSubmitDb}
        onConfirm={() => onSubmitDb(filterDate, selectedOps)}
        ops={selectedOps.join(", ")}
        filterDate={filterDate}
        dataVencimento={formatDateBR(baixaItem.data_vencimento.slice(0, 10))}
        isAntecipada={isAntecipada}
        diasAteVencimento={diasAteVencimento}
        loading={isSubmittingDb}
      />
    </Card>
  );
}
